<template>
    <div
        class="cgc-wrap"
        :class="{ 'cgc-disabled': disabled, 'cgc-active': isActive }"
        @mouseenter="onEnter"
        @mouseleave="onLeave"
        @mousedown="isActive = true"
        @mouseup="isActive = false"
        @mouseleave.capture="isActive = false"
        @click="handleClick"
        :role="disabled ? 'img' : 'button'"
        :tabindex="disabled ? -1 : 0"
        @keydown.enter.space.prevent="handleClick"
        :aria-label="disabled ? game.name + ' – Coming Soon' : 'Play ' + game.name"
    >
        <!-- ── Media area ── -->
        <div class="cgc-media">
            <img
                v-if="bgImage"
                :src="bgImage"
                :alt="game.name"
                class="cgc-img"
                draggable="false"
            />

            <!-- Hover video preview -->
            <video
                v-if="videoSrc"
                ref="hoverVideo"
                class="cgc-hover-video"
                muted
                loop
                playsinline
                :src="videoSrc"
                preload="metadata"
            />

            <!-- Coming Soon badge -->
            <div v-if="disabled" class="cgc-coming-soon">
                <span>Coming Soon</span>
            </div>

            <!-- Translucent Play overlay (visible on hover, hidden when disabled) -->
            <transition name="cgc-overlay">
                <div v-if="isHovered && !disabled" class="cgc-play-overlay">
                    <button class="cgc-play-btn" tabindex="-1" @click.stop="handleClick">
                        <svg class="cgc-play-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M8 5v14l11-7z"/>
                        </svg>
                        <span>Play</span>
                    </button>
                </div>
            </transition>

            <!-- Neon border inner glow ring (CSS-only, activated via .cgc-active on wrapper) -->
            <div class="cgc-glow-ring" aria-hidden="true" />
        </div>

        <!-- ── Footer: title + Recent Wins bar ── -->
        <div class="cgc-footer">
            <div class="cgc-footer-row">
                <span class="cgc-title">{{ game.name }}</span>
                <span class="cgc-wins-pct" :class="winsClass">{{ displayPercent }}%</span>
            </div>

            <div class="cgc-bar-track" :aria-label="'Recent wins: ' + displayPercent + '%'" role="progressbar" :aria-valuenow="displayPercent" aria-valuemin="0" aria-valuemax="100">
                <div
                    class="cgc-bar-fill"
                    :style="{ width: displayPercent + '%' }"
                />
                <div class="cgc-bar-shimmer" aria-hidden="true" />
            </div>

            <span class="cgc-bar-label">Recent Wins</span>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';

    // ── Constants for Recent Wins computation ──────────────────────────────
    /** Raw outcome value representing a 2× multiplier (outcome is stored ×100). */
    const WIN_OUTCOME_THRESHOLD = 200;
    /** Number of most-recent rounds used to compute the Crash win rate. */
    const WIN_RECENT_ROUNDS = 10;

    // ── Constants for the animated jitter used on non-Crash games ─────────
    /** Starting range lower bound (%). */
    const JITTER_MIN_PERCENT = 62;
    /** Random spread added to starting value (%). */
    const JITTER_INIT_RANGE = 16;
    /** Slight downward bias keeps the bar from drifting too high (0.5 = neutral). */
    const JITTER_BIAS = 0.48;
    /** Maximum per-tick swing in either direction (%). */
    const JITTER_MAGNITUDE = 6;
    /** Upper clamp (%). */
    const JITTER_MAX_PERCENT = 82;
    /** Lower clamp (%). */
    const JITTER_FLOOR_PERCENT = 52;
    /** How often the jitter fires (ms). */
    const JITTER_INTERVAL_MS = 3200;

    export default {
        name: 'CasinoGameCard',

        props: {
            game: {
                type: Object,
                required: true
            },
            bgImage: {
                type: String,
                default: ''
            },
            videoSrc: {
                type: String,
                default: ''
            },
            disabled: {
                type: Boolean,
                default: false
            },
            /** Override computed win% with a static value (0-100). If omitted, computed from store. */
            recentWinsPercent: {
                type: Number,
                default: null
            }
        },

        data() {
            return {
                isHovered: false,
                isActive: false,
                /** Animated percentage used for non-Crash games */
                animatedPercent: 65,
                jitterTimer: null
            };
        },

        computed: {
            ...mapGetters(['crashHistory']),

            /**
             * Win percentage shown in the progress bar.
             * - Crash: derived from last ≤MAX_RECENT_ROUNDS rounds in crashHistory
             *   (win = outcome ≥ WIN_OUTCOME_THRESHOLD, i.e. ≥ 2×).
             * - Everything else: recentWinsPercent prop, or animatedPercent.
             */
            winsPercent() {
                if (this.recentWinsPercent !== null) {
                    return Math.min(100, Math.max(0, Math.round(this.recentWinsPercent)));
                }
                if (this.game.name === 'Crash' && this.crashHistory && this.crashHistory.length > 0) {
                    const recent = this.crashHistory.slice(0, WIN_RECENT_ROUNDS);
                    const wins = recent.filter(g => (g.outcome || 0) >= WIN_OUTCOME_THRESHOLD).length;
                    return Math.round((wins / recent.length) * 100);
                }
                return this.animatedPercent;
            },

            displayPercent() {
                return this.winsPercent;
            },

            /** Colour-code the percentage label based on value */
            winsClass() {
                if (this.winsPercent >= 70) return 'wins-high';
                if (this.winsPercent >= 45) return 'wins-mid';
                return 'wins-low';
            }
        },

        watch: {
            isHovered(val) {
                const v = this.$refs.hoverVideo;
                if (!v) return;
                if (val) {
                    v.play().catch(() => {});
                } else {
                    v.pause();
                    v.currentTime = 0;
                }
            }
        },

        mounted() {
            // For games without a live history (e.g. Mines), slowly jitter the
            // animated percentage to create a "live data" visual impression.
            if (this.game.name !== 'Crash' && this.recentWinsPercent === null) {
                this.animatedPercent = JITTER_MIN_PERCENT + Math.round(Math.random() * JITTER_INIT_RANGE);
                this.jitterTimer = setInterval(() => {
                    const delta = (Math.random() - JITTER_BIAS) * JITTER_MAGNITUDE;
                    this.animatedPercent = Math.min(
                        JITTER_MAX_PERCENT,
                        Math.max(JITTER_FLOOR_PERCENT, Math.round(this.animatedPercent + delta))
                    );
                }, JITTER_INTERVAL_MS);
            }
        },

        beforeDestroy() {
            if (this.jitterTimer) {
                clearInterval(this.jitterTimer);
            }
        },

        methods: {
            onEnter() {
                if (!this.disabled) this.isHovered = true;
            },
            onLeave() {
                this.isHovered = false;
                this.isActive = false;
            },
            handleClick() {
                if (!this.disabled) this.$emit('click');
            }
        }
    };
</script>

<style scoped>
/* ─────────────────────────────────────────────
   Card shell
───────────────────────────────────────────── */
.cgc-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 166px;
    height: 222px;
    border-radius: 14px;
    cursor: pointer;
    outline: none;
    user-select: none;

    /* Soft dark neumorphism */
    background: #1c1240;
    border: 1.5px solid rgba(255, 255, 255, 0.06);
    box-shadow:
        -4px -4px 10px rgba(255, 255, 255, 0.05),
        6px  6px  18px rgba(0,   0,   0,   0.55),
        inset 0 0 0 0 transparent;

    /* Puffy spring – main hover lift */
    transition:
        transform       0.5s  cubic-bezier(0.34, 1.56, 0.64, 1),
        box-shadow      0.4s  cubic-bezier(0.25, 0.46, 0.45, 0.94),
        border-color    0.3s  ease;
}

/* ── hover state: neon glow ── */
.cgc-wrap:not(.cgc-disabled):hover {
    transform: translateY(-9px) scale(1.06);
    border-color: rgba(162, 89, 255, 0.65);
    box-shadow:
        0    0    0    1.5px rgba(162, 89, 255, 0.55),
        0    0   16px       rgba(162, 89, 255, 0.40),
        0    0   38px       rgba(162, 89, 255, 0.18),
        -4px -4px 10px      rgba(255, 255, 255, 0.06),
        8px  12px 30px      rgba(0,   0,   0,   0.65);
}

/* ── active / click-press state ── */
.cgc-wrap:not(.cgc-disabled):active,
.cgc-wrap.cgc-active:not(.cgc-disabled) {
    transform: translateY(-4px) scale(1.02);
    transition:
        transform    0.12s ease,
        box-shadow   0.12s ease;
    box-shadow:
        0    0    0   1.5px rgba(162, 89, 255, 0.5),
        0    0   10px      rgba(162, 89, 255, 0.35),
        6px  8px  20px     rgba(0, 0, 0, 0.5);
}

.cgc-wrap.cgc-disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

/* ─────────────────────────────────────────────
   Media area  (~75 % of card height)
───────────────────────────────────────────── */
.cgc-media {
    flex: 1;
    position: relative;
    border-radius: 12px 12px 0 0;
    overflow: hidden;
    min-height: 0;
}

.cgc-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    filter: brightness(0.86) saturate(1.1);
    transition: filter 0.35s ease;
}

.cgc-wrap:not(.cgc-disabled):hover .cgc-img {
    filter: brightness(1.15) saturate(1.25);
}

/* Hover video overlay */
.cgc-hover-video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease;
    z-index: 2;
}

.cgc-wrap:not(.cgc-disabled):hover .cgc-hover-video {
    opacity: 1;
}

/* Subtle inner gradient scrim (always visible) */
.cgc-media::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0.04) 0%,
        rgba(0, 0, 0, 0.28) 100%
    );
    pointer-events: none;
    z-index: 3;
    transition: opacity 0.35s ease;
}

.cgc-wrap:not(.cgc-disabled):hover .cgc-media::after {
    opacity: 0.5;
}

/* Neon border glow ring (cosmetic inset ring to enhance the neon effect) */
.cgc-glow-ring {
    position: absolute;
    inset: 0;
    border-radius: 12px 12px 0 0;
    pointer-events: none;
    z-index: 5;
    box-shadow: inset 0 0 0 0 transparent;
    transition: box-shadow 0.4s ease;
}

.cgc-wrap:not(.cgc-disabled):hover .cgc-glow-ring {
    box-shadow: inset 0 0 16px rgba(162, 89, 255, 0.22);
}

/* ─────────────────────────────────────────────
   Play overlay
───────────────────────────────────────────── */
.cgc-play-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(10, 4, 28, 0.30);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    z-index: 6;
    border-radius: 12px 12px 0 0;
}

.cgc-play-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 9px 20px;
    border: 1.5px solid rgba(162, 89, 255, 0.60);
    border-radius: 22px;
    background: rgba(162, 89, 255, 0.18);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.6px;
    cursor: pointer;
    white-space: nowrap;
    box-shadow:
        0 0 14px rgba(162, 89, 255, 0.35),
        inset 0 1px 0 rgba(255, 255, 255, 0.12);
    transition:
        transform    0.18s cubic-bezier(0.34, 1.56, 0.64, 1),
        box-shadow   0.18s ease,
        background   0.18s ease;
}

.cgc-play-btn:hover,
.cgc-play-btn:focus-visible {
    transform: scale(1.08);
    background: rgba(162, 89, 255, 0.32);
    box-shadow:
        0 0 22px rgba(162, 89, 255, 0.55),
        inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.cgc-play-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    filter: drop-shadow(0 0 4px rgba(162, 89, 255, 0.7));
}

/* Overlay enter/leave transition */
.cgc-overlay-enter-active {
    transition: opacity 0.22s ease, transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.cgc-overlay-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
}
.cgc-overlay-enter,
.cgc-overlay-leave-to {
    opacity: 0;
    transform: scale(0.92);
}

/* ─────────────────────────────────────────────
   Coming Soon badge
───────────────────────────────────────────── */
.cgc-coming-soon {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(10, 4, 28, 0.55);
    backdrop-filter: blur(2px);
    z-index: 7;
    border-radius: 12px 12px 0 0;
}

.cgc-coming-soon span {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    color: rgba(255, 255, 255, 0.55);
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    padding: 5px 10px;
}

/* ─────────────────────────────────────────────
   Footer
───────────────────────────────────────────── */
.cgc-footer {
    flex-shrink: 0;
    padding: 8px 10px 9px;
    background: rgba(0, 0, 0, 0.22);
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 0 0 13px 13px;
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.cgc-footer-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 6px;
}

.cgc-title {
    font-size: 12px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.88);
    letter-spacing: 0.4px;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cgc-wins-pct {
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
    letter-spacing: 0.3px;
    transition: color 0.4s ease;
}

.wins-high { color: #7ef7a0; }
.wins-mid  { color: #f5d078; }
.wins-low  { color: #f07070; }

/* ─────────────────────────────────────────────
   Progress bar
───────────────────────────────────────────── */
.cgc-bar-track {
    position: relative;
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.10);
    overflow: hidden;
}

.cgc-bar-fill {
    height: 100%;
    border-radius: 2px;
    background: linear-gradient(90deg, #796BFB 0%, #c084ff 100%);
    /* Smooth width transition (data-driven) */
    transition: width 0.85s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: width;
}

/* Shimmer sweep */
@keyframes cgc-shimmer {
    0%   { transform: translateX(-100%); opacity: 0.5; }
    50%  { opacity: 0.9; }
    100% { transform: translateX(400%);  opacity: 0.5; }
}

.cgc-bar-shimmer {
    position: absolute;
    top: 0;
    left: 0;
    width: 28%;
    height: 100%;
    background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.45) 50%,
        transparent 100%
    );
    animation: cgc-shimmer 2.8s ease-in-out infinite;
    pointer-events: none;
    border-radius: 2px;
}

.cgc-bar-label {
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: rgba(255, 255, 255, 0.35);
    margin-top: -2px;
}

/* ─────────────────────────────────────────────
   Large-card layout variant (used by AllGames large-cards-layout)
   Parent sets width/height on the slot, but we also expose a class hook.
───────────────────────────────────────────── */
.cgc-wrap.cgc-large {
    width: 260px;
    height: 280px;
}

/* ─────────────────────────────────────────────
   Responsive
───────────────────────────────────────────── */
@media only screen and (max-width: 480px) {
    .cgc-wrap {
        width: 148px;
        height: 198px;
    }

    .cgc-play-btn {
        padding: 7px 14px;
        font-size: 12px;
    }

    .cgc-title {
        font-size: 11px;
    }
}
</style>
